"use client";

import type {TooltipContentProps} from "@heroui/react";
import type {Ref} from "react";

import {Button, Tooltip, Typography, cn} from "@heroui/react";
import {CheckIcon, CopyIcon} from "@project/icons-web";
import {useEffect, useRef, useState} from "react";

export interface CopyButtonProps {
  className?: string;
  copiedDuration?: number;
  copiedText?: string;
  copyText?: string;
  tooltipPlacement?: TooltipContentProps["placement"];
  value: string;
}

export interface CopyTextProps extends React.HTMLAttributes<HTMLDivElement> {
  children: string;
  className?: string;
  copiedDuration?: number;
  copiedText?: string;
  copyText?: string;
  textClassName?: string;
  ref?: Ref<HTMLDivElement>;
}

export function CopyButton({
  className,
  copiedDuration = 2000,
  copiedText = "Copied!",
  copyText = "Copy",
  tooltipPlacement = "top",
  value,
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = () => {
    void navigator.clipboard.writeText(value);
    setCopied(true);

    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopied(false), copiedDuration);
  };

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  const tooltipContent = copied ? copiedText : copyText;

  return (
    <Tooltip delay={0}>
      <Button
        isIconOnly
        variant="ghost"
        size="sm"
        onPress={handleCopy}
        aria-label={tooltipContent}
        data-copied={copied || undefined}
        className={cn(
          "text-foreground/40 hover:bg-foreground/5 hover:text-foreground/70 flex size-8 cursor-pointer items-center justify-center rounded-md transition-colors data-copied:text-green-500",
          className,
        )}
      >
        {copied ? (
          <CheckIcon className="size-4" aria-hidden />
        ) : (
          <CopyIcon className="size-4" aria-hidden />
        )}
      </Button>
      <Tooltip.Content placement={tooltipPlacement}>
        <Typography className="text-sm">{tooltipContent}</Typography>
      </Tooltip.Content>
    </Tooltip>
  );
}

export function CopyText({
  children,
  className,
  copiedDuration = 2000,
  copiedText = "Copied!",
  copyText = "Copy",
  textClassName,
  ref,
  ...props
}: CopyTextProps) {
  return (
    <div ref={ref} className={cn("flex items-center gap-2", className)} {...props}>
      <span className={textClassName}>{children}</span>
      <CopyButton
        value={children}
        copiedDuration={copiedDuration}
        copiedText={copiedText}
        copyText={copyText}
      />
    </div>
  );
}
